import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMyPlanSummary } from '../../../../hooks/useUsage';
import { ProgressBar } from '@/components/ui/atoms/ProgressBar';
import type { ProgressBarIntent } from '@/components/ui/atoms/ProgressBar';

interface MeterProps {
  label: string;
  used: number;
  limit: number | null;
}

const Meter = ({ label, used, limit }: MeterProps) => {
  const isUnlimited = limit === -1 || limit === null;
  const ratio = isUnlimited ? 0 : Math.min(1, used / Math.max(1, limit ?? 1));
  const nearLimit = ratio >= 0.85;
  const atLimit = ratio >= 1;
  const intent: ProgressBarIntent = atLimit ? 'error' : nearLimit ? 'secondary' : 'primary';

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-baseline justify-between gap-2 text-[11px] font-medium font-body">
        <span className="text-dark-invirtus/60 truncate">{label}</span>
        <span className={`tabular-nums ${atLimit ? 'text-red' : nearLimit ? 'text-dark-invirtus' : 'text-dark-invirtus/70'}`}>
          {used}
          <span className="text-dark-invirtus/40">/{isUnlimited ? '∞' : limit}</span>
        </span>
      </div>
      <ProgressBar
        value={ratio * 100}
        max={100}
        size="xs"
        intent={intent}
        aria-label={label}
      />
    </div>
  );
};

export const PlanChip = ({ collapsed = false }: { collapsed?: boolean }) => {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const { data: planSummary, isLoading, error } = useMyPlanSummary();

  if (isLoading) {
    return (
      <div className={`rounded-md border border-light-lila/40 bg-light p-2.5 ${collapsed ? 'flex justify-center' : ''}`}>
        <div className="h-3 w-full animate-pulse rounded bg-dark-invirtus-soft" />
      </div>
    );
  }

  if (error || !planSummary || !planSummary.has_plan) {
    if (collapsed) {
      return (
        <button
          onClick={() => navigate('/billing')}
          title={t('plan_chip.no_plan_tooltip')}
          className="flex h-9 w-9 mx-auto items-center justify-center rounded-md bg-dark-invirtus-soft text-dark-invirtus/60 hover:bg-light-lila/40 transition-colors"
        >
          !
        </button>
      );
    }
    return (
      <button
        onClick={() => navigate('/billing')}
        className="w-full rounded-md border border-light-lila/40 bg-light p-2.5 text-left hover:border-purple-soft transition-colors"
      >
        <div className="text-xs font-semibold text-dark-invirtus font-heading">{t('plan_chip.no_plan_title')}</div>
        <div className="text-[11px] text-dark-invirtus/60 font-body mt-0.5">{t('plan_chip.no_plan_cta')}</div>
      </button>
    );
  }

  const cvUsed = planSummary.usage?.cv ?? 0;
  const cvLimit = (planSummary.limits?.cv ?? null) as number | null;
  const ivUsed = planSummary.usage?.interview ?? 0;
  const ivLimit = (planSummary.limits?.interview ?? null) as number | null;
  const tsUsed = planSummary.usage?.talent_search ?? 0;
  const tsLimit = (planSummary.limits?.talent_search ?? null) as number | null;
  const tuUsed = planSummary.usage?.talent_unlock ?? 0;
  const tuLimit = (planSummary.limits?.talent_unlock ?? null) as number | null;
  const jobUsed = planSummary.usage?.job ?? 0;
  const jobLimit = (planSummary.limits?.job ?? null) as number | null;

  const cvRatio =
    cvLimit === null || cvLimit === -1 ? 0 : Math.min(1, cvUsed / Math.max(1, cvLimit));
  const ivRatio =
    ivLimit === null || ivLimit === -1 ? 0 : Math.min(1, ivUsed / Math.max(1, ivLimit));
  const jobRatio =
    jobLimit === null || jobLimit === -1 ? 0 : Math.min(1, jobUsed / Math.max(1, jobLimit));
  const maxRatio = Math.max(cvRatio, ivRatio, jobRatio);

  if (collapsed) {
    const dotColor = maxRatio >= 1 ? 'bg-red' : maxRatio >= 0.85 ? 'bg-dark-invirtus' : 'bg-purple';
    return (
      <button
        onClick={() => navigate('/billing')}
        title={t('plan_chip.usage_tooltip', {
          cvUsed,
          cvLimit: cvLimit ?? '∞',
          ivUsed,
          ivLimit: ivLimit ?? '∞',
        })}
        className="flex h-9 w-9 mx-auto items-center justify-center rounded-md bg-light border border-light-lila/40 hover:border-purple-soft transition-colors"
      >
        <span className={`h-2 w-2 rounded-full ${dotColor}`} />
      </button>
    );
  }

  return (
    <div className="w-full rounded-md border border-light-lila/40 bg-light overflow-hidden">
      <div className="px-2.5 pt-2.5 pb-3.5">
        <div className="mb-2">
          <span className="text-[11px] font-semibold uppercase tracking-wider text-dark-invirtus/50 font-body">
            {planSummary.expired ? t('plan_chip.expired') : t('plan_chip.current_plan')}
          </span>
        </div>
        <div className="flex flex-col gap-2">
          <Meter label={t('plan_chip.jobs')} used={jobUsed} limit={jobLimit} />
          <Meter label={t('plan_chip.resumes')} used={cvUsed} limit={cvLimit} />
          <Meter label={t('plan_chip.interviews')} used={ivUsed} limit={ivLimit} />
          <Meter label={t('plan_chip.talent_search')} used={tsUsed} limit={tsLimit} />
          <Meter label={t('plan_chip.talent_unlock')} used={tuUsed} limit={tuLimit} />
        </div>
      </div>
      <button
        type="button"
        onClick={() => navigate('/billing')}
        className="w-full border-t border-light-lila/40 px-2.5 py-2 text-left text-[11px] font-medium text-dark-invirtus/70 hover:bg-dark-invirtus-soft/60 hover:text-dark-invirtus transition-colors flex items-center justify-between font-body"
      >
        <span>{t('plan_chip.plans_and_billing')}</span>
        <span aria-hidden>→</span>
      </button>
    </div>
  );
};
